// Internal: every customer at a glance, for Vikba ApS (ADMIN_EMAILS only, D-061).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link } from "react-router";
import { ApiError, api, post, timeAgo } from "../api";

interface Usage {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

interface Customer {
  id: string;
  name: string;
  website: string | null;
  plan: string;
  paidPlan: string;
  trialEndsAt: string | null;
  /** A plan given by hand from this page (D-079). */
  granted: { plan: string; until: string | null; note: string | null } | null;
  subscriptionStatus: string | null;
  founding: boolean;
  agencyParent: string | null;
  demo: boolean;
  createdAt: string;
  owner: { email: string; name: string | null } | null;
  members: number;
  lastSeenAt: string | null;
  lastActivityAt: string | null;
  instagram: {
    username: string;
    status: string;
    lastSyncedAt: string | null;
    error: string | null;
  } | null;
  posts: { total: number; live: number; toReview: number; discover: number };
  walls: {
    total: number;
    live: number;
    updatedAt: string | null;
    publishedAt: string | null;
    views30d: number;
    opens30d: number;
  };
  rights: { sent: number; granted: number };
  ai: { apps: string[]; lastUsedAt: string | null; actions30d: number; apiKeys: number };
  claude: { period: Usage; allTime: Usage };
  revenueMonthlyEur: number;
}

interface Overview {
  days: number;
  totals: {
    customers: number;
    paying: number;
    trials: number;
    activeLast7d: number;
    usingAi: number;
    liveWalls: number;
    revenueMonthlyEur: number;
    claudeCostUsd: number;
    claudeCalls: number;
    /** Customers' videos kept before permission (D-090). */
    heldVideos: { files: number; bytes: number };
  };
  customers: Customer[];
}

type SortKey = "created" | "seen" | "cost" | "revenue" | "posts" | "views" | "name";

const usd = (n: number) => (n < 0.01 && n > 0 ? "< $0.01" : `$${n.toFixed(2)}`);
const eur = (n: number) => `€${n.toFixed(n % 1 ? 2 : 0)}`;
const num = (n: number) => n.toLocaleString("en-GB");
const tokens = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${Math.round(n / 1000)}k`
      : `${n}`;
const ago = (iso: string | null) => (iso ? timeAgo(iso) : "never");

function sortValue(c: Customer, key: SortKey): number | string {
  switch (key) {
    case "name":
      return c.name.toLowerCase();
    case "seen":
      return c.lastSeenAt ? new Date(c.lastSeenAt).getTime() : 0;
    case "cost":
      return c.claude.period.costUsd;
    case "revenue":
      return c.revenueMonthlyEur;
    case "posts":
      return c.posts.total;
    case "views":
      return c.walls.views30d;
    default:
      return new Date(c.createdAt).getTime();
  }
}

const day = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** Whether a given plan still counts. */
const grantLive = (c: Customer) =>
  Boolean(c.granted && (!c.granted.until || new Date(c.granted.until) > new Date()));

function PlanCell({ c }: { c: Customer }) {
  const trial = c.trialEndsAt && new Date(c.trialEndsAt) > new Date();
  return (
    <div className="stack" style={{ gap: 2 }}>
      <span style={{ textTransform: "capitalize", fontWeight: 700 }}>{c.plan}</span>
      <span className="small muted">
        {grantLive(c)
          ? `Given${c.granted!.until ? ` until ${day(c.granted!.until)}` : ", no end"}`
          : trial
            ? `Trial ends ${new Date(c.trialEndsAt!).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
            : c.subscriptionStatus
              ? c.subscriptionStatus.replace("_", " ")
              : c.agencyParent
                ? "Under an agency"
                : "No subscription"}
        {c.founding ? " · founding" : ""}
      </span>
    </div>
  );
}

/** Gives the brand a plan by hand, or takes it back (D-079). Stripe never changes it. */
function GivePlan({ c }: { c: Customer }) {
  const qc = useQueryClient();
  const live = grantLive(c);
  const [plan, setPlan] = useState(live ? c.granted!.plan : "pro");
  const [until, setUntil] = useState(live && c.granted!.until ? c.granted!.until.slice(0, 10) : "");
  const [note, setNote] = useState(live ? (c.granted!.note ?? "") : "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const save = async (give: boolean) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await post<{ summary: string }>(`/v1/admin/customers/${c.id}/plan`, {
        plan: give ? plan : null,
        until: give && until ? until : null,
        note: give && note.trim() ? note.trim() : null,
      });
      setMsg(r.summary);
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <select
          className="input"
          style={{ width: "auto" }}
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          aria-label="Plan to give"
        >
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="agency">Agency</option>
        </select>
        <label className="row small" style={{ gap: 6 }}>
          until
          <input
            className="input"
            style={{ width: "auto" }}
            type="date"
            value={until}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setUntil(e.target.value)}
            aria-label="Last day of the plan"
          />
        </label>
        <input
          className="input"
          style={{ maxWidth: 240 }}
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why, e.g. pays by invoice"
          aria-label="Note"
        />
        <button type="button" className="btn sm" disabled={busy} onClick={() => save(true)}>
          {live ? "Change" : "Give plan"}
        </button>
        {live && (
          <button
            type="button"
            className="btn secondary sm"
            disabled={busy}
            onClick={() => save(false)}
          >
            Take it back
          </button>
        )}
      </div>
      <span className="small muted">
        {msg ??
          "Leave the date empty for no end. The brand gets this plan or what it pays for, whichever is higher, and is never charged for it."}
      </span>
    </div>
  );
}

function Details({ c, days }: { c: Customer; days: number }) {
  const rows: [string, React.ReactNode][] = [
    [
      "Plan by hand",
      c.demo ? (
        "Demo walls are always Pro"
      ) : (
        <>
          {grantLive(c) && (
            <p className="small" style={{ margin: "0 0 6px" }}>
              Given {c.granted!.plan[0]!.toUpperCase() + c.granted!.plan.slice(1)}
              {c.granted!.until ? ` until ${day(c.granted!.until)}` : ", no end"}
              {c.granted!.note ? ` · ${c.granted!.note}` : ""}
            </p>
          )}
          <GivePlan c={c} />
        </>
      ),
    ],
    ["Owner", c.owner ? `${c.owner.name ?? ""} ${c.owner.email}`.trim() : "None"],
    ["Team", `${c.members} ${c.members === 1 ? "person" : "people"}`],
    ["Signed up", new Date(c.createdAt).toLocaleDateString("en-GB")],
    ["Last seen in the app", ago(c.lastSeenAt)],
    ["Last change in Brandwall", ago(c.lastActivityAt)],
    [
      "Instagram",
      c.instagram
        ? `@${c.instagram.username} · ${c.instagram.status} · last checked ${ago(c.instagram.lastSyncedAt)}`
        : "Not connected",
    ],
    ["Instagram problem", c.instagram?.error ?? "None"],
    [
      "Posts",
      `${num(c.posts.total)} in total · ${num(c.posts.live)} live · ${num(c.posts.toReview)} to review · ${num(c.posts.discover)} in Discover`,
    ],
    [
      "Walls",
      `${c.walls.live} live of ${c.walls.total} · changed ${ago(c.walls.updatedAt)} · published ${ago(c.walls.publishedAt)}`,
    ],
    [
      "Wall views, 30 days",
      `${num(c.walls.views30d)} views · ${num(c.walls.opens30d)} posts opened`,
    ],
    ["Permission", `${num(c.rights.sent)} asked · ${num(c.rights.granted)} said yes`],
    [
      "AI apps (MCP)",
      c.ai.apps.length
        ? `${c.ai.apps.join(", ")} · last used ${ago(c.ai.lastUsedAt)} · ${c.ai.actions30d} actions in 30 days`
        : "None connected",
    ],
    ["API keys", `${c.ai.apiKeys}`],
    [
      `Claude, ${days} days`,
      `${num(c.claude.period.calls)} calls · ${tokens(c.claude.period.inputTokens)} in · ${tokens(c.claude.period.outputTokens)} out · ${usd(c.claude.period.costUsd)}`,
    ],
    [
      "Claude, all time",
      `${num(c.claude.allTime.calls)} calls · ${tokens(c.claude.allTime.inputTokens)} in · ${tokens(c.claude.allTime.outputTokens)} out · ${usd(c.claude.allTime.costUsd)}`,
    ],
    [
      "Pays per month",
      c.revenueMonthlyEur ? `${eur(c.revenueMonthlyEur)} (list price, before VAT)` : "Nothing",
    ],
  ];
  return (
    <dl className="sa-details">
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function SuperAdmin() {
  const [days, setDays] = useState(30);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("created");
  const [showDemos, setShowDemos] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const data = useQuery({
    queryKey: ["admin", "customers", days],
    queryFn: () => api<Overview>(`/v1/admin/customers?days=${days}`),
    refetchInterval: 60_000,
  });
  if (data.error)
    return (
      <div className="onb">
        {data.error instanceof ApiError && data.error.status === 404
          ? "Page not found"
          : data.error.message}
      </div>
    );
  const t = data.data?.totals;
  const words = q.trim().toLowerCase();
  const list = (data.data?.customers ?? [])
    .filter((c) => showDemos || !c.demo)
    .filter(
      (c) =>
        !words ||
        [c.name, c.website, c.owner?.email, c.owner?.name, c.instagram?.username]
          .filter(Boolean)
          .some((s) => s!.toLowerCase().includes(words)),
    )
    .sort((a, b) => {
      const x = sortValue(a, sort);
      const y = sortValue(b, sort);
      if (sort === "name") return String(x).localeCompare(String(y));
      return (y as number) - (x as number);
    });

  const th = (key: SortKey, label: string) => (
    <th>
      <button
        type="button"
        className={`sa-sort ${sort === key ? "on" : ""}`}
        onClick={() => setSort(key)}
        aria-pressed={sort === key}
      >
        {label}
        {sort === key ? " ↓" : ""}
      </button>
    </th>
  );

  return (
    <div className="content sa">
      <div className="stack" style={{ gap: 18 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <img src="/logo.svg" alt="Brandwall" style={{ height: 26 }} />
          <h1 className="spacer">Super admin</h1>
          <Link to="/admin/demos" className="small">
            Demo walls
          </Link>
          <Link to="/admin/blog" className="small">
            Blog
          </Link>
          <Link to="/" className="small">
            Back to the app
          </Link>
        </div>

        <div className="sa-kpis">
          {(
            [
              ["Customers", t ? num(t.customers) : "…"],
              ["Paying", t ? num(t.paying) : "…"],
              ["On a trial", t ? num(t.trials) : "…"],
              ["Seen in 7 days", t ? num(t.activeLast7d) : "…"],
              ["Use AI apps", t ? num(t.usingAi) : "…"],
              ["Live walls", t ? num(t.liveWalls) : "…"],
              ["Revenue a month", t ? eur(t.revenueMonthlyEur) : "…"],
              [`Claude, ${days} days`, t ? usd(t.claudeCostUsd) : "…"],
              [
                "Videos kept before a yes",
                t
                  ? `${num(t.heldVideos.files)} · ${(t.heldVideos.bytes / 1e9).toFixed(1)} GB`
                  : "…",
              ],
            ] as [string, string][]
          ).map(([label, value]) => (
            <div key={label} className="card sa-kpi">
              <span className="small muted">{label}</span>
              <b>{value}</b>
            </div>
          ))}
        </div>

        <div className="row wrap" style={{ gap: 12 }}>
          <input
            className="input"
            style={{ maxWidth: 320 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, website, Instagram"
            aria-label="Search customers"
          />
          <select
            className="input"
            style={{ width: "auto" }}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Period for Claude costs"
          >
            <option value={7}>Claude costs: 7 days</option>
            <option value={30}>Claude costs: 30 days</option>
            <option value={90}>Claude costs: 90 days</option>
            <option value={365}>Claude costs: 1 year</option>
          </select>
          <label className="row small" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={showDemos}
              onChange={(e) => setShowDemos(e.target.checked)}
            />
            Show demo walls
          </label>
          <span className="small muted spacer" style={{ textAlign: "right" }}>
            {list.length} shown · Claude prices are Anthropic's list prices in US dollars
          </span>
        </div>

        <div className="card sa-wrap">
          <table className="table sa-table">
            <thead>
              <tr>
                {th("name", "Customer")}
                <th>Plan</th>
                {th("seen", "Last seen")}
                <th>Instagram</th>
                {th("posts", "Posts")}
                {th("views", "Walls")}
                <th>AI apps</th>
                {th("cost", "Claude")}
                {th("revenue", "Pays")}
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <Fragment key={c.id}>
                  <tr
                    className={`click ${open === c.id ? "on" : ""}`}
                    onClick={() => setOpen(open === c.id ? null : c.id)}
                  >
                    <td>
                      <b>{c.name}</b>
                      {c.demo && (
                        <span className="pill warn" style={{ marginLeft: 6 }}>
                          Demo
                        </span>
                      )}
                      <div className="small muted">{c.owner?.email ?? c.website ?? ""}</div>
                    </td>
                    <td>
                      <PlanCell c={c} />
                    </td>
                    <td className="small">{ago(c.lastSeenAt)}</td>
                    <td className="small">
                      {c.instagram ? (
                        <>
                          @{c.instagram.username}
                          <div className={c.instagram.status === "connected" ? "muted" : "sa-bad"}>
                            {c.instagram.status === "connected"
                              ? `checked ${ago(c.instagram.lastSyncedAt)}`
                              : "needs connecting again"}
                          </div>
                        </>
                      ) : (
                        <span className="muted">Not connected</span>
                      )}
                    </td>
                    <td className="small">
                      {num(c.posts.live)} live
                      <div className="muted">
                        {num(c.posts.total)} in total · {num(c.posts.toReview)} to review
                      </div>
                    </td>
                    <td className="small">
                      {c.walls.live} live · {num(c.walls.views30d)} views
                      <div className="muted">changed {ago(c.walls.updatedAt)}</div>
                    </td>
                    <td className="small">
                      {c.ai.apps.length ? (
                        c.ai.apps.join(", ")
                      ) : (
                        <span className="muted">None</span>
                      )}
                      {c.ai.apps.length > 0 && (
                        <div className="muted">used {ago(c.ai.lastUsedAt)}</div>
                      )}
                    </td>
                    <td className="small">
                      <b>{usd(c.claude.period.costUsd)}</b>
                      <div className="muted">
                        {num(c.claude.period.calls)} calls ·{" "}
                        {tokens(c.claude.period.inputTokens + c.claude.period.outputTokens)} tokens
                      </div>
                    </td>
                    <td className="small">
                      {c.revenueMonthlyEur ? (
                        eur(c.revenueMonthlyEur)
                      ) : (
                        <span className="muted">€0</span>
                      )}
                    </td>
                  </tr>
                  {open === c.id && (
                    <tr className="sa-open">
                      <td colSpan={9}>
                        <Details c={c} days={days} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {data.isSuccess && list.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No customers match.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
